import { useCallback, useMemo, useState } from "react";

/** Per viewer, in localStorage: "open" or "closed". Absent or unreadable means open. */
export const SOCIAL_TOPIC_OVERVIEW_STORAGE_KEY = "fairgen.topic.overview";

export interface UseOverviewCollapsedResult {
  isOpen: boolean;
  setOpen: (isOpen: boolean) => void;
  toggle: () => void;
}

function readStoredOpen(): boolean {
  try {
    return window.localStorage.getItem(SOCIAL_TOPIC_OVERVIEW_STORAGE_KEY) !== "closed";
  } catch {
    return true;
  }
}

function writeStoredOpen(isOpen: boolean): void {
  try {
    window.localStorage.setItem(SOCIAL_TOPIC_OVERVIEW_STORAGE_KEY, isOpen ? "open" : "closed");
  } catch {
    // Blocked storage (a private window, a preview): the choice lasts this visit only.
  }
}

/**
 * Whether the Topic overview (tier 2) is open, remembered per viewer. Every
 * storage read and write is wrapped in try/catch.
 */
export function useOverviewCollapsed(): UseOverviewCollapsedResult {
  const [isOpen, setIsOpen] = useState<boolean>(readStoredOpen);

  const setOpen = useCallback((next: boolean) => {
    setIsOpen(next);
    writeStoredOpen(next);
  }, []);

  const toggle = useCallback(() => {
    setIsOpen((current) => {
      const next = !current;
      writeStoredOpen(next);
      return next;
    });
  }, []);

  return useMemo(() => ({ isOpen, setOpen, toggle }), [isOpen, setOpen, toggle]);
}
